import { useQuery } from '@tanstack/react-query';

import { OFFER_TYPE_TACTICAL_AO_HOTEL } from '~/consts/offerTypes';

import OffersService from '~/services/OffersService';
import SearchService from '~/services/SearchService';

export const resolveLpcOfferIdQueryKeys = {
  all: ['resolveLpcOfferId'] as const,
  byName: (name: string) => [...resolveLpcOfferIdQueryKeys.all, name] as const,
};

/**
 * Resolves a property name to an LPC (tactical_ao_hotel) offer ID.
 *
 * The typeahead endpoint does not distinguish Flash from LPC offers, so when
 * it returns a Flash offer we fall back to the offers API filtered by type
 * to locate the correct LPC offer for the same property.
 */
export function useResolveLpcOfferId(propertyName: string | null) {
  return useQuery({
    queryKey: resolveLpcOfferIdQueryKeys.byName(propertyName ?? ''),
    queryFn: () => resolveToLpcOfferId(propertyName),
    enabled: !!propertyName,
  });
}

export async function resolveToLpcOfferId(propertyName: string): Promise<string | null> {
  const { result } = await SearchService.typeahead(propertyName, {
    type: ['le_property'],
    brand: 'luxuryescapes',
    region: 'AU',
    limit: 1,
    priority: 'current',
  });

  const offerId = result?.[0]?.offerId ?? null;
  if (!offerId) {
    return null;
  }

  const offerResponse = await OffersService.getOffer(offerId);
  const offer = offerResponse.result;

  if (offer.type === OFFER_TYPE_TACTICAL_AO_HOTEL) {
    return offerId;
  }

  // The typeahead returned a non-LPC offer (e.g. Flash). Search the offers API
  // by property name filtered to LPC type to find the correct offer.
  const lpcSearchResponse = await OffersService.searchOffersByType(propertyName, OFFER_TYPE_TACTICAL_AO_HOTEL);
  const lpcOffer = lpcSearchResponse.result?.[0];
  return lpcOffer?.id_salesforce_external ?? offerId;
}
